import { ReactNode, Suspense, lazy } from "react";
import styled, { css } from "styled-components/macro";
import { BORDER_RADIUS, ERROR_COLOR, THEME_COLOR_DARK } from "../vars";
import { useSkipioApi } from "../api";
import { FILESTACK_KEY } from "../env";
import { darken } from "polished";
import { defer } from "@synvox/api";
import { Icon } from "./Icons";
import { IconButton } from "./Button";
import Tooltip from "./Tooltip";

const THEME_COLOR = darken(0.175, "#40C0FE");

const emojiRegex = /^(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])+$/;

const Audio = lazy(() => import("./Audio"));

type TImageAvatar = {
  imageUrl?: string;
  initials: string;
  tooltip?: string;
};

type TIconAvatar = {
  icon: ReactNode;
  tooltip?: string;
};

type SharedMessageProps = {
  author?: string;
  focusButton?: {
    icon: ReactNode;
    onClick: () => void;
    tooltip: string;
  };
  children?: ReactNode;
  direction?: "inbound" | "outbound";
  error?: string;
  focused?: boolean;
  metadata?: ReactNode | string;
  primaryAvatar?: TImageAvatar | TIconAvatar;
  secondaryAvatar?: TImageAvatar | TIconAvatar;
  stackedWithNextMessage?: boolean;
  stackedWithPrevMessage?: boolean;
};

type SmsProps = {
  children: ReactNode;
};

type MediaProps = {
  mediaThumbnail?: boolean;
  mediaType: string;
  mediaUrl: string;
};

type VoicemailProps = {
  voicemailId: string;
};

export function SmsMessage(props: SharedMessageProps & SmsProps) {
  return (
    <Message {...props}>
      {typeof props.children === "string" &&
      emojiRegex.test(props.children.trim()) ? (
        <Emojis>{props.children.trim()}</Emojis>
      ) : (
        <Bubble direction={props.direction || "outbound"} data-ignore="true">
          {props.children}
          {!props.stackedWithNextMessage && (
            <BubblePointer
              direction={props.direction || "outbound"}
              width="13px"
              height="14px"
              viewBox="0 0 13 14"
            >
              <path d="M13,9 C10,12 8,14 0,14 C5,10 4,5 4,0 L4,0 L13,9 Z" />
            </BubblePointer>
          )}
        </Bubble>
      )}
    </Message>
  );
}

export function MediaMessage({
  mediaThumbnail,
  mediaType,
  mediaUrl,
  ...sharedProps
}: SharedMessageProps & MediaProps) {
  mediaType = mediaType || "image";
  let messageContent;

  if (mediaType.startsWith("image")) {
    messageContent = (
      <div key={mediaUrl}>
        <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
          <Image
            mediaSize={mediaThumbnail ? "thumbnail" : "full"}
            src={`https://process.filestackapi.com/${FILESTACK_KEY}/compress/${mediaUrl}`}
            alt="Attachment"
          />
        </a>
      </div>
    );
  } else if (mediaType.startsWith("video")) {
    messageContent = (
      <div key={mediaUrl}>
        <Image
          mediaSize={mediaThumbnail ? "thumbnail" : "full"}
          as="video"
          src={mediaUrl}
          controls
        />
      </div>
    );
  } else {
    messageContent = (
      <div key={mediaUrl}>
        <UnknownMedia>Unable to show attachment</UnknownMedia>
      </div>
    );
  }

  return <Message {...sharedProps}>{messageContent}</Message>;
}

export function VoicemailMessage(props: SharedMessageProps & VoicemailProps) {
  const skipioApi = useSkipioApi();

  const { data: voicemail } = defer(
    () => (skipioApi.recordings[props.voicemailId].get() || { data: null }).data
  );

  return (
    <Message {...props}>
      {!!voicemail && (
        <Suspense fallback={null}>
          <AudioContainer>
            <Audio src={voicemail.audioUrl} key={voicemail.audioUrl} />
          </AudioContainer>
        </Suspense>
      )}
    </Message>
  );
}

function Message({
  author,
  focusButton,
  children,
  direction = "outbound",
  error = undefined,
  focused = true,
  metadata,
  primaryAvatar,
  secondaryAvatar,
  stackedWithNextMessage = false,
  stackedWithPrevMessage = false,
  ...restProps
}: SharedMessageProps) {
  return (
    <Container direction={direction} focused={focused} {...restProps}>
      {author && !stackedWithPrevMessage && (
        <Author data-ignore="true" indented={!!primaryAvatar}>
          {author}
        </Author>
      )}
      <Row reversed={direction === "outbound"}>
        <AvatarContainer visible={!stackedWithNextMessage}>
          {primaryAvatar &&
            ("icon" in primaryAvatar ? (
              <IconAvatar data-ignore="true" title={primaryAvatar.tooltip}>
                {primaryAvatar.icon}
              </IconAvatar>
            ) : (
              <ImageAvatar
                data-ignore="true"
                title={primaryAvatar.tooltip}
                initials={primaryAvatar.initials}
                avatarUrl={primaryAvatar.imageUrl}
              />
            ))}
          {secondaryAvatar &&
            ("icon" in secondaryAvatar ? (
              <IconAvatar
                data-ignore="true"
                title={secondaryAvatar.tooltip}
                secondary
              >
                {secondaryAvatar.icon}
              </IconAvatar>
            ) : (
              <ImageAvatar
                secondary
                data-ignore="true"
                title={secondaryAvatar.tooltip}
                initials={secondaryAvatar.initials}
                avatarUrl={secondaryAvatar.imageUrl}
              />
            ))}
        </AvatarContainer>
        <MessageContentContainer focused={focused}>
          {children}
        </MessageContentContainer>
        {focusButton && (
          <ButtonContainer>
            <Tooltip label={focusButton.tooltip}>
              <FocusButton onClick={focusButton.onClick}>
                {focusButton.icon}
              </FocusButton>
            </Tooltip>
          </ButtonContainer>
        )}
      </Row>
      {metadata && !stackedWithNextMessage && (
        <Metadata data-ignore="true" indented={!!primaryAvatar}>
          {metadata}
        </Metadata>
      )}
      {Boolean(error) && <Error indented={!!primaryAvatar}>{error}</Error>}
    </Container>
  );
}

const Bubble = styled.div<{
  direction: "inbound" | "outbound";
}>`
  border-radius: 15px;
  padding: 7px 10px;
  ${(p) =>
    p.direction === "inbound"
      ? css`
          background: #e9e9e9;
          color: #333;
        `
      : css`
          background-color: ${THEME_COLOR};
          color: white;
        `}
  position: relative;
  display: inline-block;
  text-align: left;
  line-height: 1.3em;
  max-width: 90ch;
  overflow-wrap: break-word;
  white-space: pre-wrap;
`;

const BubblePointer = styled.svg<{
  direction: "inbound" | "outbound";
}>`
  position: absolute;
  pointer-events: none;
  bottom: 0;
  ${(p) =>
    p.direction === "inbound"
      ? css`
          fill: #e9e9e9;
          left: 0;
          transform: translateX(-4px);
        `
      : css`
          fill: ${THEME_COLOR};
          right: 0;
          transform: translateX(4px) scaleX(-1);
        `}
`;

const ButtonContainer = styled.div`
  align-self: center;
  margin: 0 4px;
`;

const FocusButton = styled(IconButton)`
  visibility: hidden;
  * {
    transition-duration: 0s;
  }
`;

const MessageContentContainer = styled.div<{ focused: boolean }>`
  margin-bottom: 1px;
  opacity: ${(p) => (p.focused ? 1.0 : 0.5)};
  transition-duration: 0.5s;
  transition-property: opacity;
`;

export const Container = styled.div<{
  direction: "inbound" | "outbound";
  focused: boolean;
}>`
  text-align: ${(p) => (p.direction === "inbound" ? "left" : "right")};
  &:hover ${FocusButton} {
    visibility: ${(p) => (p.focused ? "hidden" : "visible")};
  }
`;

const Author = styled.div<{ indented: boolean }>`
  padding: 0 10px;
  font-size: 11px;
  color: #808080;
  margin: ${(p) => (p.indented ? "0 40px 1px" : "0 10px 1px")};
  line-height: 1.25em;
  font-weight: bold;
`;

const Metadata = styled.div<{ indented: boolean }>`
  padding: 0 10px;
  margin: ${(p) => (p.indented ? "2px 40px 10px" : "2px 10px 10px")};
  font-size: 10px;
  color: #909090;
  b {
    font-weight: bold;
  }
`;

const Error = styled(Metadata)`
  color: ${ERROR_COLOR};
`;

export const Variable = styled.span`
  display: inline-block;
  background: rgba(0, 0, 0, 0.1);
  border-radius: ${BORDER_RADIUS};
  padding: 0px 8px;
  margin: 0px auto;
`;

const Image = styled.img<{ mediaSize?: "full" | "thumbnail" }>`
  border-radius: 7px;
  height: 300px;
  background: #e9e9e9;
  margin-top: 3px;
  object-fit: contain;
  max-width: 500px;
  border: 1px solid #f6f6f6;
  ${(p) =>
    p.mediaSize === "thumbnail" &&
    css`
      max-width: 200px;
      height: 150px;
    `}
`;

const UnknownMedia = styled.div`
  border-radius: 7px;
  padding: 20px;
  color: #888;
  background: #e9e9e9;
  margin-top: 3px;
  display: inline-block;
`;

const Emojis = styled.div`
  font-size: 50px;
  margin-bottom: 8px;
  margin-top: 4px;
`;

const AudioContainer = styled.div`
  background: #fafafa;
  border: 1px solid #d7d7d9;
  border-radius: 6px;
  margin-bottom: 4px;
  width: 300px;
`;

const AvatarContainer = styled.div<{ visible: boolean }>`
  display: flex;
  visibility: ${(p) => (p.visible ? "visible" : "hidden")};
  align-self: flex-end;
  align-items: center;
  justify-content: center;
  margin: 0 5px;
  position: relative;
`;

const Avatar = styled.div<{ secondary?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: 30px;
  border-radius: 1000px;
  ${(p) =>
    p.secondary &&
    css`
      position: absolute;
      right: -8px;
      top: -8px;
      height: 20px;
      width: 20px;
      border: 1px solid white;
    `}
`;

const ImageAvatar = styled(Avatar)<{ initials: string; avatarUrl?: string }>`
  background-color: #aaa;
  background-size: cover;
  background-position: 50% 50%;
  background-image: ${(p) => (p.avatarUrl ? `url(${p.avatarUrl})` : "none")};
  ${(p) =>
    !p.avatarUrl &&
    p.initials &&
    css`
      &:after {
        display: block;
        content: "${p.initials}";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 11px;
        color: white;
      }
    `}
`;

const IconAvatar = styled(Avatar)`
  background-color: ${THEME_COLOR_DARK};
  ${Icon} {
    width: 18px;
    height: 18px;
    color: white;
  }
`;

const Row = styled.div<{ reversed: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.reversed ? "row-reverse" : "row")};
`;
